import * as Sentry from "@sentry/react";
import { ORGANIZATION_ID, SUBDOMAIN } from "../const";

/**
 * Sentry initialisieren - muss ganz frueh in index.tsx aufgerufen werden.
 *
 * Die Umgebungsvariablen SENTRY_DSN, SENTRY_ENVIRONMENT und
 * SENTRY_TRACES_SAMPLE_RATE werden ueber das Webpack DefinePlugin
 * als process.env-Konstanten injiziert.
 */
/**
 * Platzhalter aus `.env.example` (CI kopiert sie unverändert nach `.env`, #1316).
 * Das SDK würde die ungültige DSN sonst mit `console.error("Invalid Sentry Dsn")`
 * verwerfen und bei jedem Seitenaufruf Rauschen in Faro/Loki erzeugen.
 */
export function isUsableSentryDsn(dsn: string | undefined): boolean {
    return Boolean(dsn) && /^https?:\/\/[^@]+@/.test(dsn as string);
}

export function initSentry(): void {
    if (!isUsableSentryDsn(process.env.SENTRY_DSN)) {
        return;
    }

    const tracesSampleRate = parseFloat(process.env.SENTRY_TRACES_SAMPLE_RATE || "0");

    Sentry.init({
        dsn: process.env.SENTRY_DSN,
        environment: process.env.SENTRY_ENVIRONMENT,
        release: `kursorganizer-webapp@${process.env.APP_VERSION || "unknown"}`,
        tracesSampleRate: isNaN(tracesSampleRate) ? 0 : tracesSampleRate,
        initialScope: {
            tags: {
                app: "admin",
                tenant_id: ORGANIZATION_ID || "unknown",
                subdomain: SUBDOMAIN || "unknown",
            },
        },
        beforeSend(event, hint) {
            const error = hint.originalException;

            // ResizeObserver-Fehler filtern (werden bereits global unterdrueckt)
            if (error instanceof Error && error.message.includes("ResizeObserver")) {
                return null;
            }
            if (typeof error === "string" && error.includes("ResizeObserver")) {
                return null;
            }

            // Cross-Origin Script-Fehler filtern (3rd Party, kein Stack Trace)
            if (
                event.message === "Script error." &&
                !event.exception?.values?.[0]?.stacktrace?.frames?.length
            ) {
                return null;
            }

            // Maintenance-Fehler filtern (bereits separat behandelt)
            const errorMessage =
                event.exception?.values?.[0]?.value?.toLowerCase() || "";
            if (
                errorMessage.includes("service unavailable") ||
                errorMessage.includes("maintenance") ||
                errorMessage.includes("wartung")
            ) {
                return null;
            }

            return event;
        },
    });
}

/**
 * User-Kontext setzen bei Login.
 * @param userId - FusionAuth User-ID
 * @param email - E-Mail-Adresse des Users
 * @param firstName - Vorname des Users
 * @param lastName - Nachname des Users
 */
export function setSentryUser(
    userId: string,
    email?: string,
    firstName?: string,
    lastName?: string,
): void {
    const username =
        firstName || lastName
            ? [firstName, lastName].filter(Boolean).join(" ")
            : undefined;

    Sentry.setUser({
        id: userId,
        ...(email ? { email } : {}),
        ...(username ? { username } : {}),
    });
}

/**
 * User-Kontext loeschen bei Logout.
 */
export function clearSentryUser(): void {
    Sentry.setUser(null);
}

/**
 * Tenant-Tag dynamisch aktualisieren, z.B. nach Org-Wechsel.
 * @param orgId - Die Organization-ID
 */
export function setSentryOrganization(orgId: string): void {
    Sentry.setTag("tenant_id", orgId);
}

/**
 * Erkennt erwartete Auth-/Autorisierungsfehler, die kein App-Bug sind,
 * aber fuer UX-Analyse getrackt werden sollen.
 *
 * Gegenueber der End-User-App zusaetzlich:
 * - code "FORBIDDEN" (Rollen-System der Admin-App)
 * - Nachrichten mit "permission"/"Permission"
 */
function isExpectedAuthError(message: string, code: string): boolean {
    return (
        message.includes("Wrong password") ||
        message.includes("User not found") ||
        message.includes("Invalid credentials") ||
        message.includes("permission") ||
        message.includes("Permission") ||
        code === "UNAUTHENTICATED" ||
        code === "FORBIDDEN"
    );
}

/**
 * Extrahiert Username/Email aus den GraphQL-Variablen fuer Auth-Fehler-Tracking.
 */
function extractUserIdentifier(
    variables?: Record<string, unknown>,
): string | undefined {
    if (!variables) {
        return undefined;
    }

    const thirdPartyInput = variables.thirdPartyLoginInput as
        | Record<string, unknown>
        | undefined;

    return (
        (thirdPartyInput && typeof thirdPartyInput.username === "string"
            ? thirdPartyInput.username
            : undefined) ||
        (typeof variables.username === "string"
            ? variables.username
            : undefined) ||
        (typeof variables.email === "string" ? variables.email : undefined) ||
        (typeof variables.loginId === "string"
            ? variables.loginId
            : undefined)
    );
}

/**
 * GraphQL-Fehler mit Kontext an Sentry senden.
 *
 * - Maintenance- und Schema-Fehler werden ignoriert
 * - Erwartete Auth-Fehler werden als Warning geloggt (kein Bug, aber UX-relevant)
 * - Alle anderen Fehler werden als Exception erfasst
 */
export function captureGraphQLError(
    errors: readonly { message?: string; extensions?: Record<string, unknown> }[],
    operationName?: string,
    variables?: Record<string, unknown>,
): void {
    for (const err of errors) {
        const message = (err.message as string) || "";
        const code = ((err.extensions && err.extensions.code) as string) || "";

        // Maintenance- und Schema-Fehler nicht melden
        if (
            code === "SERVICE_UNAVAILABLE" ||
            code === "MAINTENANCE" ||
            message.includes("Cannot query field") ||
            message.includes("Unknown field")
        ) {
            continue;
        }

        // Erwartete Auth-/Autorisierungsfehler als Warning loggen
        if (isExpectedAuthError(message, code)) {
            Sentry.withScope((scope) => {
                scope.setLevel("warning");
                scope.setTag("graphql_operation", operationName || "unknown");
                scope.setTag("graphql_error_code", code || "none");
                scope.setTag("auth_error_type", message);
                const userIdentifier = extractUserIdentifier(variables);
                if (userIdentifier) {
                    scope.setTag("attempted_user", userIdentifier);
                }
                scope.setContext("auth_failure", {
                    operationName,
                    errorMessage: message,
                    errorCode: code,
                    attemptedUser: userIdentifier,
                });
                Sentry.captureMessage(
                    `Auth Failure [${operationName}]: ${message}`,
                    "warning",
                );
            });
            continue;
        }

        // Unerwartete Fehler als Exception melden
        Sentry.withScope((scope) => {
            scope.setTag("graphql_operation", operationName || "unknown");
            scope.setTag("graphql_error_code", code || "none");
            scope.setContext("graphql", {
                operationName,
                errorMessage: message,
                errorCode: code,
                variables: variables
                    ? JSON.stringify(variables).substring(0, 500)
                    : undefined,
            });
            Sentry.captureException(
                new Error(`GraphQL Error [${operationName}]: ${message}`),
            );
        });
    }
}

/**
 * Sicherer Zugriff auf die erste GraphQL Error Message.
 * Ersetzt das unsichere Pattern: JSON.stringify(error.graphQLErrors[0].message, null, 4)
 */
export function getGraphQLErrorMessage(
    error: { graphQLErrors?: { message?: string }[]; networkError?: { message?: string }; message?: string } | null | undefined,
): string | undefined {
    if (!error) {
        return undefined;
    }

    const gqlMessage =
        error.graphQLErrors &&
        error.graphQLErrors.length > 0 &&
        error.graphQLErrors[0].message
            ? error.graphQLErrors[0].message
            : undefined;

    const networkMessage =
        error.networkError && error.networkError.message
            ? error.networkError.message
            : undefined;

    return gqlMessage || networkMessage || error.message;
}
